export type TranslationKey =
  | 'apiConfiguration'
  | 'provider'
  | 'selectProvider'
  | 'paidTierDescription'
  | 'freeTierDescription'
  | 'apiKey'
  | 'getApiKey'
  | 'endpointUrl'
  | 'configuring'
  | 'saveConfiguration'
  | 'configured'
  | 'login'
  | 'signUp'
  | 'name'
  | 'email'
  | 'password'
  | 'importantInfo'
  | 'apiKeys'
  | 'chatHistory'
  | 'deleteChat'
  | 'training'
  | 'acceptTerms'
  | 'continueWith'
  | 'google'
  | 'gemini'
  | 'needAccount'
  | 'alreadyHaveAccount'
  | 'signInWithEmail'
  | 'emailLinkSent'
  | 'checkEmail'
  | 'invalidEmailLink'
  | 'orCreateAccount'
  | 'continue'
  | 'typeMessage'
  | 'history'
  | 'authenticationFailed'
  | 'googleSignInFailed'
  | 'selectModelToStart'
  | 'enhancePrompt'
  | 'underDevelopment'
  | 'noConversationsYet'
  | 'welcomeToAiChat'
  | 'getStartedWithPowerfulAiModels'
  | 'apiDescription'
  | 'supportedAiModels'
  | 'readyToConfigure'
  | 'backToHome'
  | 'addFirstProvider'
  | 'cookieConsentTitle'
  | 'cookieConsentDescription'
  | 'necessaryCookies'
  | 'analyticsCookies'
  | 'marketingCookies'
  | 'acceptCookies'
  | 'acceptNecessaryOnly'
  | 'newChat'
  | 'modelCardChat'
  | 'analytics'
  | 'analyticsDescription'
  | 'documents'
  | 'documentProcessing'
  | 'customModels'
  | 'customModelsDescription'
  | 'xaiGrok'
  | 'xaiDescription'
  | 'cerebras'
  | 'cerebrasDescription'
  | 'localModels'
  | 'localModelsDescription'
  | 'tutorialLink'
  | 'model'
  | 'welcomeMessage'
  | 'activeFiles'
  | 'insufficientFunds'
  | 'invalidApiKey'
  | 'apiNotConfigured'
  | 'emailVerificationRequired'
  | 'refreshPage'
  | 'welcomeSignup'
  | 'almostDone'
  | 'dataSecurityInfo'
  | 'seeDetails'
  | 'secureApiKeys'
  | 'secureHistory'
  | 'permanentDeletion'
  | 'noTraining'
  | 'uploadTextFiles'
  | 'webSearchEnabled'
  | 'enableWebSearch';

export type TranslationType = {
  [key in TranslationKey]: string;
};

export type Translations = {
  [key: string]: TranslationType;
};

export const translations: Translations = {
  en: {
    login: 'Login',
    signUp: 'Sign Up',
    name: 'Name',
    email: 'Email',
    password: 'Password',
    importantInfo: 'Important Information:',
    apiKeys: 'API keys are stored in Firebase encrypted',
    chatHistory: 'Chat history is stored in Firebase',
    deleteChat: 'You can delete each chat and it will be deleted from server',
    training: 'Data isn\'t being used for training purposes',
    acceptTerms: 'I understand and accept these terms',
    continueWith: 'Or continue with',
    gemini: 'Gemini',
    google: 'Google',
    needAccount: 'Need an account? Sign Up',
    alreadyHaveAccount: 'Already have an account? Login',
    typeMessage: 'Type your message...',
    history: 'History',
    authenticationFailed: 'Authentication failed',
    googleSignInFailed: 'Failed to sign in with Google',
    selectModelToStart: 'Select model & 💬',
    webSearchEnabled: 'Web search enabled',
    enableWebSearch: 'Enable web search',
    enhancePrompt: 'Enhance prompt',
    underDevelopment: 'Under development',
    noConversationsYet: 'No conversations yet',
    addFirstProvider: 'You need to add your first provider to start chatting',
    apiConfiguration: 'API Configuration',
    provider: 'Provider',
    selectProvider: 'Select a provider',
    apiKey: 'API Key',
    getApiKey: 'Get API Key',
    endpointUrl: 'Endpoint URL',
    configuring: 'Configuring...',
    saveConfiguration: 'Save Configuration',
    configured: 'Configured',
    welcomeToAiChat: 'Welcome to Camaleon',
    getStartedWithPowerfulAiModels: 'Get started with powerful AI models with your own connectors or APIs. Consumption per use, without monthly payments and with free models. Test it with no charge with Free Tier models.',
    apiDescription: 'An AI API allows you to access advanced machine learning models through a simple interface. Configure your API to unlock the full potential of this application.',
    supportedAiModels: 'Supported AI Models',
    readyToConfigure: 'I\'m ready to add my 1st API',
    backToHome: 'Back to Home',
    paidTierDescription: 'Requires adding a credit card. Charges will apply from the first prompt based on inbound and outbound pricing.',
    freeTierDescription: 'No credit card required. Free to use at all times.',
    cookieConsentTitle: 'We use cookies',
    cookieConsentDescription: 'We use cookies to enhance your experience. Please choose which types of cookies you allow:',
    necessaryCookies: 'Necessary cookies (always enabled)',
    analyticsCookies: 'Analytics cookies',
    marketingCookies: 'Marketing cookies',
    acceptCookies: 'Accept selected',
    acceptNecessaryOnly: 'Accept necessary only',
    newChat: 'New Chat',
    modelCardChat: 'Chat with powerful AI models',
    analytics: 'Advanced Analytics',
    analyticsDescription: 'Get detailed insights and analytics about your AI usage',
    documents: 'Document Processing',
    documentProcessing: 'Process and analyze documents with AI',
    customModels: 'Custom Models',
    customModelsDescription: 'Upload and use your own custom AI models',
    xaiGrok: 'XAI Grok',
    xaiDescription: 'Explainable AI for better understanding of model decisions',
    cerebras: 'Cerebras',
    cerebrasDescription: 'High-performance AI for enterprise use cases',
    localModels: 'Local Models',
    localModelsDescription: 'Run AI models locally on your device',
    tutorialLink: 'View Tutorial',
    model: 'Model',
    welcomeMessage: 'What can I do for you?',
    activeFiles: 'Active files in this chat:',
    insufficientFunds: 'Your balance (${{balance}}) is insufficient for this operation (cost: ${{cost}}). Please add more funds or configure your own API key.',
    invalidApiKey: 'Invalid API key. Please check your configuration.',
    apiNotConfigured: 'API not properly configured. Please check your settings.',
    signInWithEmail: 'Sign in with Email Link',
    emailLinkSent: 'Email link sent! Check your inbox to complete sign in.',
    checkEmail: 'Check your email to complete sign in',
    invalidEmailLink: 'Invalid email link. Please try again.',
    emailVerificationRequired: 'Email Verification Required',
    refreshPage: 'Refresh Page',
    orCreateAccount: 'Or create an account with your email',
    continue: 'Continue',
    welcomeSignup: 'Welcome! Start using our app quickly and securely.',
    almostDone: 'Almost done! Fill in these details:',
    dataSecurityInfo: 'We securely store your data and allow you to delete it anytime.',
    seeDetails: 'See details...',
    secureApiKeys: 'API keys| are securely stored and encrypted.',
    secureHistory: 'Chat history| is saved and can be deleted anytime.',
    permanentDeletion: 'Deleted chats are| permanently removed| from the server.',
    noTraining: 'Your data is not used| for training purposes.',
    uploadTextFiles: 'Upload text files (.txt, .md, .csv, .json) - Max 20MB'
  },
  es: {
    login: 'Iniciar sesión',
    signUp: 'Registrarse',
    name: 'Nombre',
    email: 'Correo electrónico',
    password: 'Contraseña',
    importantInfo: 'Información importante:',
    apiKeys: 'Las claves API se almacenan en Firebase encriptadas',
    chatHistory: 'El historial de chat se almacena en Firebase',
    deleteChat: 'Puedes eliminar cada chat y se borrará del servidor',
    training: 'Los datos no se utilizan para fines de entrenamiento',
    acceptTerms: 'Entiendo y acepto estos términos',
    continueWith: 'O continúa con',
    gemini: 'Gemini',
    google: 'Google',
    needAccount: '¿Necesitas una cuenta? Regístrate',
    alreadyHaveAccount: '¿Ya tienes una cuenta? Inicia sesión',
    typeMessage: 'Escribe tu mensaje...',
    history: 'Histórico',
    authenticationFailed: 'Error de autenticación',
    googleSignInFailed: 'Error al iniciar sesión con Google',
    selectModelToStart: 'Elige modelo para 💬',
    webSearchEnabled: 'Búsqueda web activada',
    enableWebSearch: 'Activar búsqueda web',
    enhancePrompt: 'Mejorar el prompt',
    underDevelopment: 'En desarrollo',
    noConversationsYet: 'No hay conversaciones aún',
    apiConfiguration: 'Configuración de API',
    provider: 'Proveedor',
    selectProvider: 'Seleccionar proveedor',
    apiKey: 'Clave API',
    getApiKey: 'Obtener clave API',
    endpointUrl: 'URL del endpoint',
    configuring: 'Configurando...',
    saveConfiguration: 'Guardar configuración',
    configured: 'Configurado',
    welcomeToAiChat: 'Bienvenido a Camaleon',
    getStartedWithPowerfulAiModels: 'Comienza con potentes modelos de IA con tus propios conectores o APIs. Consumo por uso, sin mensualidades y con modelos gratuitos. Prueba los modelos Free Tier sin ningún tipo de cargos.',
    apiDescription: 'Una API de IA te permite acceder a modelos avanzados de aprendizaje automático a través de una interfaz simple. Configura tu API para desbloquear todo el potencial de esta aplicación.',
    supportedAiModels: 'Modelos de IA Soportados',
    readyToConfigure: 'Estoy listo para añadir mi primera API',
    backToHome: 'Volver al Inicio',
    paidTierDescription: 'Requiere añadir una tarjeta de crédito. Se aplicarán cargos desde el primer prompt según los precios de entrada y salida.',
    freeTierDescription: 'No se requiere tarjeta de crédito. Gratis para usar en todo momento.',
    addFirstProvider: 'Necesitas añadir tu primer proveedor para empezar a chatear',
    cookieConsentTitle: 'Usamos cookies',
    cookieConsentDescription: 'Usamos cookies para mejorar tu experiencia. Por favor, elige qué tipos de cookies permites:',
    necessaryCookies: 'Cookies necesarias (siempre activadas)',
    analyticsCookies: 'Cookies de análisis',
    marketingCookies: 'Cookies de marketing',
    acceptCookies: 'Aceptar seleccionadas',
    acceptNecessaryOnly: 'Aceptar solo las necesarias',
    newChat: 'Nuevo Chat',
    modelCardChat: 'Chatea con potentes modelos de IA',
    analytics: 'Análisis Avanzado',
    analyticsDescription: 'Obtén información detallada y análisis sobre tu uso de IA',
    documents: 'Procesamiento de Documentos',
    documentProcessing: 'Procesa y analiza documentos con IA',
    customModels: 'Modelos Personalizados',
    customModelsDescription: 'Sube y usa tus propios modelos de IA personalizados',
    xaiGrok: 'XAI Grok',
    xaiDescription: 'IA explicable para una mejor comprensión de las decisiones del modelo',
    cerebras: 'Cerebras',
    cerebrasDescription: 'IA de alto rendimiento para casos de uso empresarial',
    localModels: 'Modelos Locales',
    localModelsDescription: 'Ejecuta modelos de IA localmente en tu dispositivo',
    tutorialLink: 'Ver Tutorial',
    model: 'Modelo',
    welcomeMessage: '¿Cómo te puedo ayudar?',
    activeFiles: 'Archivos activos en este chat:',
    insufficientFunds: 'Tu saldo (${{balance}}) es insuficiente para esta operación (costo: ${{cost}}). Por favor, añade más fondos o configura tu propia clave API.',
    invalidApiKey: 'Clave API inválida. Por favor, revisa tu configuración.',
    apiNotConfigured: 'API no configurada correctamente. Por favor, revisa tu configuración.',
    signInWithEmail: 'Iniciar sesión con enlace por correo',
    emailLinkSent: '¡Enlace enviado! Revisa tu correo para completar el inicio de sesión.',
    checkEmail: 'Revisa tu correo para completar el inicio de sesión',
    invalidEmailLink: 'Enlace inválido. Por favor, inténtalo de nuevo.',
    emailVerificationRequired: 'Verificación de Correo Requerida',
    refreshPage: 'Actualizar Página',
    orCreateAccount: 'O crea una cuenta con tu correo',
    continue: 'Continuar',
    welcomeSignup: '¡Bienvenido! Comienza a usar nuestra app de forma rápida y segura.',
    almostDone: '¡Casi listo! Completa estos detalles:',
    dataSecurityInfo: 'Almacenamos tus datos de forma segura y puedes eliminarlos en cualquier momento.',
    seeDetails: 'Ver detalles...',
    secureApiKeys: 'Las claves API| se almacenan de forma segura y encriptada.',
    secureHistory: 'El historial de chat| se guarda y puede eliminarse en cualquier momento.',
    permanentDeletion: 'Los chats eliminados se borran| permanentemente| del servidor.',
    noTraining: 'Tus datos no se utilizan| para fines de entrenamiento.',
    uploadTextFiles: 'Subir archivos de texto (.txt, .md, .csv, .json) - Max 20MB'
  }
};